import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Moment from 'moment';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { Formik } from 'formik';
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
  AppNoInfo,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import { toFormData } from '../../../minions/forms';
import Translator from '../../../layout/components/helpers/Translator';
import { path, axiosPath } from '../../../routing/helpers/router';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import ImageCropper from '../../../layout/components/form/ImageCropper';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';

const ModuleChemicalTradingProductNewScreen = () => {
  const { moduleBridgeHex } = useParams();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const [product, setProduct] = useState(null);
  const [productOptions, setProductOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moduleData, setModuleData] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [productTemplates, setProductTemplates] = useState(null);
  const [productFormInitialValues, setProductFormInitialValues] = useState({
    image: '',
    comercialName: '',
    chemicalName: '',
    containerType: null,
    quantity: '',
    expireDate: null,
    closeDate: null,
    productUse: '',
    features: '',
    saleReason: '',
    storePlace: '',
    risk: '',
    isCompanyName: true,
    isRepackable: false,
    isArchived: false,
    files: null,
    deletedFiles: [],
  });

  // > LOAD PRODUCT TEMPLATES //
  useEffect(() => {
    axios
      .post(
        axiosPath('api_r_user_module_chemicalTrading_products_get_template'),
        { moduleBridgeHex }
      )
      .then((r) => {
        if (isMounted.current) {
          setProductTemplates(r.data);
          setShowTemplateModal(true);
          setLoading(false);
        }
      });
  }, []);
  // > LOAD PRODUCT TEMPLATES //

  // > LOAD PRODUCTS OPTIONS //
  useEffect(() => {
    axios
      .post(axiosPath('api_r_user_module_chemicalTrading_product_options'), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) {
          setProductOptions(r.data);
        }
      });
  }, []);
  // > LOAD PRODUCTS OPTIONS //

  // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(axiosPath('api_r_user_module_chemicalTrading_getModuleData'), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) setModuleData(r.data);
      });
  }, []);
  // > LOAD MODULE DATA //

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    comercialName: Yup.string().required(),
    chemicalName: Yup.string().required(),
    containerType: Yup.number().nullable().required(),
    quantity: Yup.number().nullable().required(),
    expireDate: Yup.string().required(),
    saleReason: Yup.string().required(),
  });
  // < FORM SCHEMA

  // < SAVE FORM
  const addProduct = async (values) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_chemicalTrading_product_new'),
        toFormData({
          ...values,
          moduleBridgeHex,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (err) {}
  };

  const handleSaveProduct = (values) => {
    setIsSaving(true);
    addProduct(values)
      .then(() => {
        if (isMounted.current) {
          navigate(
            path({
              name: 'user_module_chemicalTrading_product_list',
              params: { moduleBridgeHex },
            })
          );
          AppNotificationAlert({
            title: 'Completado',
            message: 'Se ha añadido un nuevo producto',
            type: 'success',
            duration: 1000,
          });
        }
      })
      .catch(() => {
        AppNotificationAlert({
          title: 'Error',
          message: 'Se ha producido un error',
          type: 'danger',
          duration: 1000,
        });

        if (isMounted.current) setIsSaving(false);
      });
  };

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt))
      setFieldValue(
        'deletedFiles',
        deletedFiles.filter((s) => s !== salt)
      );
    else setFieldValue('deletedFiles', [...deletedFiles, salt]);
  };
  // < SAVE FORM

  // < TEMPLATE FORM
  const validationTemplateSchema = Yup.object().shape({
    product: Yup.number().nullable().required(),
  });

  const loadProduct = useCallback(async (id) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_chemicalTrading_product_get'),
        { moduleBridgeHex, id }
      );
      return response.data;
    } catch (err) {}
  }, []);

  const handleTemplateForm = (values) => {
    setIsSaving(true);
    loadProduct(values.product).then((res) => {
      if (isMounted.current) {
        const templateProduct = {
          ...res,
          closeDate: null,
          isArchived: null,
          files: null,
          cloneFiles: res.files,
        };
        setProduct(templateProduct);
        setProductFormInitialValues(templateProduct);
        setIsSaving(false);
        setShowTemplateModal(false);
      }
    });
  };
  // < TEMPLATE FORM

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={[
          {
            name: `${moduleData.moduleName} - ${ReactDOMServer.renderToString(
              <Translator file="module-chemicalTrading" name="products.list" />
            )}`,
            route: {
              name: 'user_module_chemicalTrading_product_list',
              params: { moduleBridgeHex },
            },
          },
          {
            name: product
              ? product.comercialName
              : ReactDOMServer.renderToString(
                  <Translator
                    file="module-chemicalTrading"
                    name="new.product"
                  />
                ),
          },
        ]}
      />
      <section className="content">
        <div className="row mt-3">
          <div className="col-12">
            <Card className="card" isArchived={product?.isArchived}>
              <div className="card-body">
                {loading ? (
                  <NoInfo icon={<SpinnerLoader size={40} />} />
                ) : (
                  <Formik
                    validateOnChange={false}
                    validateOnBlur={false}
                    enableReinitialize
                    initialValues={productFormInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSaveProduct}
                  >
                    {({ setFieldValue, handleSubmit, values, errors }) => (
                      <>
                        <div className="row">
                          <div className="col-md-6 mb-4">
                            <ImageCropper
                              cropShape="rect"
                              image={values.image}
                              width={200}
                              height={200}
                              onSave={(image) => setFieldValue('image', image)}
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="row">
                              <div className="col-lg-12">
                                <AppInput
                                  icon={
                                    <AppRemixIcon
                                      icon="font-color"
                                      noVariants
                                    />
                                  }
                                  title={
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="comercial.name"
                                    />
                                  }
                                  onChange={(value) =>
                                    setFieldValue('comercialName', value)
                                  }
                                  value={values.comercialName}
                                  error={!!errors.comercialName}
                                  isClearable
                                  required
                                />
                              </div>
                              <div className="col-lg-12">
                                <AppInput
                                  icon={
                                    <AppRemixIcon
                                      icon="font-color"
                                      noVariants
                                    />
                                  }
                                  title={
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="chemical.name"
                                    />
                                  }
                                  onChange={(value) =>
                                    setFieldValue('chemicalName', value)
                                  }
                                  value={values.chemicalName}
                                  error={!!errors.chemicalName}
                                  isClearable
                                  required
                                />
                              </div>
                              <div className="col-lg-12">
                                <AppSingleSelect
                                  options={productOptions?.containerTypes}
                                  icon={<AppRemixIcon icon="apps-2" />}
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="container.type"
                                    />
                                  )}
                                  onChange={(value) =>
                                    setFieldValue('containerType', value)
                                  }
                                  value={values.containerType}
                                  error={!!errors.containerType}
                                  isClearable
                                  required
                                />
                              </div>
                              <div className="col-lg-12">
                                <AppInputNumber
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="quantity"
                                    />
                                  )}
                                  icon={
                                    <AppRemixIcon icon="hashtag" noVariants />
                                  }
                                  value={values.quantity}
                                  onChange={(value) =>
                                    setFieldValue('quantity', value)
                                  }
                                  error={!!errors.quantity}
                                  isClearable
                                  required
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <AppDatePicker
                              title={
                                <Translator
                                  file="module-chemicalTrading"
                                  name="expire.date"
                                />
                              }
                              value={values.expireDate}
                              onChange={(date) =>
                                setFieldValue('expireDate', date)
                              }
                              error={!!errors.expireDate}
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <AppDatePicker
                              title={
                                <Translator
                                  file="module-chemicalTrading"
                                  name="close.date"
                                />
                              }
                              value={values.closeDate}
                              onChange={(date) =>
                                setFieldValue('closeDate', date)
                              }
                              maxDate={
                                product &&
                                Moment(product.lastEditionDate, 'DD/MM/YYYY')
                                  .add(1, 'year')
                                  .toDate()
                              }
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppTextArea
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="sale.reason"
                                />
                              )}
                              onChange={(v) => setFieldValue('saleReason', v)}
                              value={values.saleReason}
                              error={!!errors.saleReason}
                              isClearable
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <AppTextArea
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="contact.data"
                                />
                              )}
                              onChange={(v) => setFieldValue('contactData', v)}
                              value={values.contactData}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppTextArea
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="product.use"
                                />
                              )}
                              onChange={(v) => setFieldValue('productUse', v)}
                              value={values.productUse}
                              error={!!errors.productUse}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppTextArea
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="features"
                                />
                              )}
                              onChange={(v) => setFieldValue('features', v)}
                              value={values.features}
                              error={!!errors.features}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppInput
                              icon={<AppRemixIcon icon="map-pin" />}
                              title={
                                <Translator
                                  file="module-chemicalTrading"
                                  name="store.place"
                                />
                              }
                              onChange={(value) =>
                                setFieldValue('storePlace', value)
                              }
                              value={values.storePlace}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppInput
                              icon={<AppRemixIcon icon="alert" />}
                              title={
                                <Translator
                                  file="module-chemicalTrading"
                                  name="risk"
                                />
                              }
                              onChange={(value) => setFieldValue('risk', value)}
                              value={values.risk}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="col-form-label mr-3 pointer">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={values.isCompanyName}
                                onChange={() =>
                                  setFieldValue(
                                    'isCompanyName',
                                    !values.isCompanyName
                                  )
                                }
                                checked={values.isCompanyName}
                              />
                              <Translator
                                file="module-chemicalTrading"
                                name="show.company.name"
                              />
                            </label>
                          </div>
                          <div className="col-md-6">
                            <label className="col-form-label mr-3 pointer">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={values.isRepackable}
                                onChange={() =>
                                  setFieldValue(
                                    'isRepackable',
                                    !values.isRepackable
                                  )
                                }
                                checked={values.isRepackable}
                              />
                              <Translator
                                file="module-chemicalTrading"
                                name="is.repackable"
                              />
                            </label>
                          </div>
                          <div className="col-lg-12 mt-4">
                            <label>
                              <Translator
                                file="module-chemicalTrading"
                                name="files"
                              />
                            </label>
                            <AppFileUploader
                              isMulti
                              placeholder={
                                !values.files || values.files.length === 0
                                  ? 'Selecciona uno o varios archivos'
                                  : `${values.files.length} archivos seleccionados`
                              }
                              onChange={(opt) => setFieldValue('files', opt)}
                              btnText={
                                <Translator file="general" name="search..." />
                              }
                            />
                          </div>
                          {values.cloneFiles &&
                            Object.keys(values.cloneFiles).length > 0 && (
                              <div className="col-lg-12">
                                <div className="row">
                                  <div className="col-lg-12 mt-4">
                                    <label>
                                      <Translator
                                        file="module-chemicalTrading"
                                        name="delete.files"
                                      />
                                    </label>
                                    {Object.keys(values.cloneFiles).map(
                                      (salt) => (
                                        <div key={salt} className="mt-2">
                                          <a
                                            target="_blank"
                                            title={values.cloneFiles[salt].name}
                                            className="mr-3"
                                            href={`${global.urlData.userApiUrl}m/chemical-trading/get-product-file/${moduleBridgeHex}/${product.id}/${salt}`}
                                            rel="noreferrer"
                                          >
                                            <small>Descargar</small>
                                          </a>
                                          <label className="mb-2 pointer font-weight-light">
                                            <input
                                              type="checkbox"
                                              className="mr-1"
                                              value={salt}
                                              onChange={() =>
                                                handleDeletedFiles(
                                                  setFieldValue,
                                                  values.deletedFiles,
                                                  salt
                                                )
                                              }
                                              checked={
                                                values.deletedFiles &&
                                                values.deletedFiles[salt]
                                              }
                                            />
                                            {values.cloneFiles[salt].name}
                                          </label>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                        <div className="row">
                          <div className="col-12 text-right">
                            <button
                              className="btn btn-success mt-4"
                              type="submit"
                              onClick={handleSubmit}
                              disabled={isSaving}
                            >
                              <Translator
                                file="module-chemicalTrading"
                                name="add"
                              />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </Formik>
                )}
              </div>
            </Card>
          </div>
        </div>
        <AppModal
          show={showTemplateModal}
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-chemicalTrading"
              name="product.templates"
            />
          )}
          onClose={() => setShowTemplateModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={{
              product: null,
            }}
            validationSchema={validationTemplateSchema}
            onSubmit={handleTemplateForm}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <>
                <div className="row">
                  <div className="col-lg-12">
                    <AppSingleSelect
                      options={productTemplates}
                      value={values.product}
                      onChange={(value) => setFieldValue('product', value)}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-chemicalTrading"
                          name="products.list"
                        />
                      )}
                      icon={<AppRemixIcon icon="file-list" />}
                      error={!!errors.product}
                      hasSearchBox
                      hasSelectOptions
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-4 text-right">
                    <button
                      className="btn btn-outline-danger mr-2"
                      onClick={() => setShowTemplateModal(false)}
                      disabled={isSaving}
                    >
                      <Translator
                        file="module-chemicalTrading"
                        name="no.use.template"
                      />
                    </button>
                    <button
                      className="btn btn-success"
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isSaving}
                    >
                      <Translator
                        file="module-chemicalTrading"
                        name="use.template"
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleChemicalTradingProductNewScreen;

const Card = styled.div`
  border: ${(props) => (props.isArchived ? '1px solid red' : 'border: none')};
`;
